import { useMemo, useState } from "react";
import { useRouter } from "next/router";
import { useGames } from "@/api/game-api";
import {
  DEVICE_FILTER_OPTIONS,
  GENRE_FILTER_OPTIONS,
  PLATFORM_FILTER_OPTIONS,
  SUPPORT_TYPE_FILTER_OPTIONS,
  VR_PLATFORM_VALUES,
} from "@/constants/game-filter-options";
import { SupportTypeUtil } from "@/utils/SupportTypeUtil";
import {
  AI_POWERED_FACET,
  OFFICIAL_MOD_FACET,
  DeviceType,
  SupportType,
} from "@/services/ContentsService";
import { BhapticsContent } from "@/store/reducers/contents";
import { useLocale } from "@/store/locale-store";
import { filterSellableGames } from "@/hooks/useBhapticsContents";
import { useCurrency } from "@/hooks/useCurrency";
import { GameUtil } from "@/utils/GameUtil";

/**
 * `/games/vr/games` 의 필터 상태 + **항목별 개수**.
 *
 * ## 왜 `useBhapticsContents` 를 안 쓰나
 *
 * 도입 당시(#5416 · `f1298379a`) 두 가지가 이 화면과 안 맞았다.
 *
 * **1) 목적지가 하드코딩돼 있었다.** 그 훅의 `handleQueries`/`reset` 은
 * `pathname: "/games/search"` 로 push 했다(`useBhapticsContents.ts`). 그래서 탭 안에서
 * 필터를 적용하면 **탭 밖으로 튕겼다** — 실측(2026-08-28 dev): `/games/pc/games` 에서
 * `Support Type > Native` 적용 → `/games/search/?filter=pc,native`. 서브내비도 안내
 * 섹션도 사라진다. 여기서는 **현재 경로를 유지**한다.
 *
 * **2) 플랫폼 facet 이 덧붙기만 했다.** `useFilters` 는 경로가 `games/vr` 이면 VR 6종을
 * 목록에 **push** 했다. SDK 는 같은 그룹을 OR 로 묶으므로(실측: `quest`150 · `steam`254 ·
 * `quest,steam`**318**) 거기에 `quest` 를 더해도 좁혀지지 않는다.
 * 레일에서 플랫폼을 고르는 건 **덧셈이 아니라 교체**여야 한다.
 *
 * ⚠️ **둘 다 #5448(`3532e15ac`, 2026-08-31 — 도입과 같은 날)부터 사실이 아니다** — 목적지는
 * `router.pathname` 이 됐고, 경로 유도 facet 은 「기본값」이라 쿼리에 플랫폼 값이 있으면
 * 얹지 않는다(`useBhapticsContents.ts` 의 `useFilters` 주석). 이 훅을 따로 두는 이유로는
 * 지금 아래 「서버 질의는 한 번, 나머지는 클라」(그 훅은 **항목별 개수**를 주지 않는다)와,
 * 축마다 자기 쿼리 키(`QUERY_KEYS` — `platform`·`support`·`device`·`genre`)를 갖는 모양(그
 * 훅은 `filter=` 한 목록)이 남는다. PC 판의 같은 정정은 `use-pc-games-filters.ts` 의
 * 「⚠️ 「목적지가 하드코딩돼 있다」는 더 이상 사실이 아니다」 절.
 *
 * ## 서버 질의는 한 번, 나머지는 클라
 *
 * `by-filters` 는 페이징이 없어 **VR 전량을 한 배열로** 준다(실측 318건). 그래서
 * 기본 집합(VR 6종)만 서버에 묻고 **좁히기와 개수는 클라에서** 한다. 이점이 셋이다 —
 * 체크할 때마다 왕복하지 않고, 시안이 요구하는 **항목별 개수**를 공짜로 얻고,
 * 개수와 목록이 **같은 데이터**에서 나와 어긋날 수 없다.
 *
 * ## ⚠️ 플랫폼은 `platformList` 로 세면 안 된다
 *
 * 실측(2026-08-28 prod, VR 318건):
 *
 * ```
 *            platformList   supportedPlatforms[].platform   SDK 필터
 *   vive           0                    2                      2
 *   pico           0                   21                     21
 *   steam        245                  254                      —
 * ```
 *
 * `platformList` 는 SDK 필터의 정본이 아니다. 그걸로 세면 시안이 **명시적으로 노출하는**
 * VIVE·PICO 행이 조용히 `0` 이 된다. `supportedPlatforms[].platform` 을 쓴다.
 */

export type VrFilterGroupKey = "platform" | "supportType" | "device" | "genre";

export type VrFilterRow = {
  value: string;
  label: string;
  /** 이 항목을 **추가로** 켰을 때 남는 게임 수. 0 이면 화면에서 비활성으로 그린다. */
  count: number;
  checked: boolean;
};

export type VrFilterGroup = {
  key: VrFilterGroupKey;
  label: string;
  /** 시안 기준 초기 펼침 상태 (`29889:130588` — 지원 방식·지원 플랫폼만 펼쳐져 있다). */
  defaultOpen: boolean;
  rows: VrFilterRow[];
};

/**
 * 그룹 정의. 값 목록은 **폴백 상수에서 그대로** 가져온다 — 손으로 나열하면 #5424 가
 * 없앤 「사본이 갈라진다」를 다시 만든다.
 *
 * ⚠️ 플랫폼은 `PLATFORM_FILTER_OPTIONS`(8개) 전부가 아니라 **`VR_PLATFORM_VALUES`(6개)**
 * 로 좁힌다. `pc`·`simracing` 은 VR 이 아니다.
 *
 * ⚠️ 시안(`29889:130628`)의 플랫폼 행은 **5개**(Quest·Steam·PICO·VIVE·PSVR2)로 `rift` 가
 * 빠져 있다. 6개로 늘렸다 — `rift` 는 **41건**이라 빼면 그 게임들을 어느 체크박스로도
 * 못 거른다(`vive` 2건 · `pico` 21건 · `psvr2` 27건 · `simracing` 19건보다 많다).
 * 시안과 다른 유일한 지점이다.
 */
/**
 * VR 지원 게임 페이지에서 **숨기는** 지원 방식 facet.
 *
 * ⚠️ **공유 상수에서 지우면 안 된다.** `SUPPORT_TYPE_FILTER_OPTIONS` 는 PC 필터
 * (`use-pc-games-filters.ts`)·배지 variant·폴백 목록(`GAME_FILTER_GROUPS_FALLBACK`)이
 * 함께 쓴다. 플랫폼 그룹이 `VR_PLATFORM_VALUES` 로 좁히는 것과 같은 방식으로 **이 화면에서만**
 * 걸러낸다.
 *
 * 근거·실측은 아래 `supportType` 그룹 주석에 있다.
 */
const VR_HIDDEN_SUPPORT_TYPE_FACETS: readonly string[] = [
  AI_POWERED_FACET,
  OFFICIAL_MOD_FACET,
];

export const GROUP_DEFS: {
  key: VrFilterGroupKey;
  label: string;
  defaultOpen: boolean;
  options: { value: string; description: string }[];
}[] = [
  {
    key: "supportType",
    label: "Support Type",
    defaultOpen: true,
    /**
     * ★**`ai-powered`(#5529) · `official-mod`(#5612) 를 VR 에서 뺀다** — 작성자 요청.
     *
     * 목록은 `VR_HIDDEN_SUPPORT_TYPE_FACETS` 다. 아래 근거는 두 facet 에 공통이다 —
     * **둘 다 화면 술어로 0건이라 고를 수 없는 칩으로 남아 있었다.**
     *
     * **VR 플랫폼 6종에는 해당 게임이 없다.** 0건 옵션은 숨겨지지 않고 **비활성 칩으로
     * 남아**(`vr-games-filter-rail.tsx` 의 `disabled = row.count === 0 && !row.checked`)
     * 고를 수 없는 항목이 계속 보였다.
     *
     * 실측(2026-09-04 · 앱이 쓰는 경로 `by-filters`):
     *
     * ```
     *   prod VR   319건 · 모든 플랫폼 AIPowered      0건  ← 화면 판정
     *                    한 플랫폼이라도 AIPowered   0건  ← 넓힌 술어
     *                    VR 플랫폼에 AIPowered 가 붙은 (게임,플랫폼) 쌍 0건
     *   dev  VR    67건 · 0건
     *   prod PC    43건 · 3건   ← 술어가 유효하다는 대조군
     *                             (PUBG · VALORANT · Warzone)
     * ```
     *
     * `official-mod` 도 같은 방식으로 쟀다 — 앱이 쓰는 경로(`by-filters`)에
     * **`SupportTypeUtil.getSupportType` 를 그대로** 적용했다(아래가 경고하는
     * 「다시 유도하면 어긋난다」를 피하려고 그 함수를 복제해 돌렸다).
     * 실측(2026-09-07 prod):
     *
     * ```
     *   prod VR   319건   Native 212 · Mod 107 · OfficialMod 0 · AiPowered 0
     *                     ★화면 술어 OfficialMod  0건
     *                     넓힌 술어(Mod+official 플랫폼 쌍)  1쌍 · 게임 1건 (Minecraft)
     *   prod PC    43건   OfficialMod 21 · Mod 16 · Native 3 · AiPowered 3
     *                     ★대조군 — 술어가 유효하다(0 이 아니다)
     * ```
     *
     * ⚠️ **넓힌 술어가 0 이 아니다** — `ai-powered` 와 다른 점이다. `Minecraft` 에
     * `Mod + official` 플랫폼이 **1개** 있는데, `getSupportType` 은 「**모든** 플랫폼이
     * Mod 이고 unofficial 이 **하나도** 없을 때」만 `OfficialMod` 로 보므로 그 게임은
     * `Mod` 로 분류된다. 즉 **되살릴 조건이 명확하다** — 그 게임의 나머지 VR 플랫폼이
     * official 이 되거나 전 플랫폼 official-mod 인 VR 게임이 생기면 0 이 아니게 된다.
     *
     * ## ⚠️ 「PC 전용 기능이라」로 적지 않는다 (#5537 R1 n1)
     *
     * 초판 주석이 그렇게 적었는데 **관측을 제품 속성으로 단정한 것**이었고, 리포의
     * 두 자리가 오히려 반대를 가리킨다:
     *
     * ```
     *   vr-ath-band.tsx:178   오디오 햅틱은 PC Player · Mobile Player(Android) ·
     *                         ★VR Player 에서 지원된다 (시안 문구)
     *   docs/game-support-type-policy.md
     *                         혼합 예시가 `pc:Mod + steam:AIPowered` — ★steam 은 VR 이다
     * ```
     *
     * 즉 **구조적으로 불가능한 것이 아니라 지금 0건**이다. 되살릴 사람이 읽을 자리라
     * 그 구분이 판단을 가른다.
     *
     * ⚠️ **공유 상수에서 지우면 안 된다.** `SUPPORT_TYPE_FILTER_OPTIONS` 는 PC 필터
     * (`use-pc-games-filters.ts`)·배지 variant·폴백 목록(`GAME_FILTER_GROUPS_FALLBACK`)
     * 이 함께 쓴다. 그래서 플랫폼 그룹이 `VR_PLATFORM_VALUES` 로 좁히는 것과 같은
     * 방식으로 **이 화면에서만** 걸러낸다.
     *
     * 해당 facet 의 VR 게임이 생기면 `VR_HIDDEN_SUPPORT_TYPE_FACETS` 에서 그 값을 지운다.
     * 지우기 전에 위 실측을 **다시 돌려** 0 이 아님을 확인할 것.
     */
    options: SUPPORT_TYPE_FILTER_OPTIONS.filter(
      (o) => !VR_HIDDEN_SUPPORT_TYPE_FACETS.includes(o.value),
    ),
  },
  {
    key: "platform",
    label: "Platform",
    defaultOpen: true,
    options: PLATFORM_FILTER_OPTIONS.filter((o) =>
      (VR_PLATFORM_VALUES as readonly string[]).includes(o.value),
    ),
  },
  {
    key: "device",
    label: "Device",
    defaultOpen: false,
    /**
     * ★**`Tactosy for Hands` 는 뺀다 — 제품이 단종됐다** (#5530 · 작성자 요청).
     *
     * ## 「0건이라 뺀다」가 아니다
     *
     * 이 행은 **VR 게임 54건**이 걸린다(prod 실측 2026-09-04). 즉 데이터가 없어서가
     * 아니라 **그 기기를 살 수 없어서** 뺀다 — 고를 수는 있는데 사서 쓸 수는 없는
     * 필터가 된다.
     *
     * ## 기준: **판매중지**만 숨긴다. 품절은 유지한다
     *
     * 공개 제품 API 는 **판매중지 상품을 목록에서 제외**하고, **품절은 재고 0 으로
     * 남긴다**(`docs/pitfalls.md` 의 shop-product 항목). 그래서 두 상태가 구별된다 —
     * prod `admin-apis /api/v1/shop-products` 실측(2026-09-04):
     *
     * ```
     *   tactosy-for-hands   목록에 없음        → 판매중지  ★숨긴다
     *   tactosy-for-feet    목록에 있음·재고 0  → 품절      유지한다(재입고 가능)
     * ```
     *
     * `Tactosy for Feet`(VR 21건)를 함께 빼지 않은 이유가 이것이다. 재입고되면
     * 되살릴 필요가 없어야 하므로 「지금 살 수 있나」가 아니라 「제품이 살아 있나」로
     * 가른다.
     *
     * ## 여기서만 걸러도 되는 이유
     *
     * `DEVICE_FILTER_OPTIONS` 의 소비처는 **이 화면과 폴백 상수뿐**이다 — PC 필터에는
     * Device 그룹이 아예 없다(실측: PC 는 지원방식 칩만 그린다). 그래서 `ai-powered`
     * 때와 달리 다른 화면으로 번질 걱정은 없지만, 폴백 상수는 SDK 응답의 기준값이라
     * 건드리지 않고 **화면에서만** 뺀다.
     *
     * ⚠️ 게임 상세의 지원 기기 표기·배지는 그대로 둔다 — 기존 구매자에게는 여전히
     * 유효한 정보다. 여기서 빼는 것은 **고르는 수단**뿐이다.
     */
    options: DEVICE_FILTER_OPTIONS.filter((o) => o.value !== DeviceType.Hand),
  },
  {
    key: "genre",
    label: "Genre",
    defaultOpen: false,
    options: GENRE_FILTER_OPTIONS,
  },
];

/** 게임이 가진 플랫폼 facet 집합. 위 주석 참조 — `platformList` 가 아니다. */
export const platformsOf = (game: BhapticsContent): string[] =>
  (game.supportedPlatforms ?? [])
    .map((p) => p.platform)
    .filter((p): p is string => !!p);

/**
 * 게임의 지원 방식 facet **한 개**. 리포 표준(`SupportTypeUtil.getSupportType`)을 그대로
 * 쓴다 — `every`/`some` 규칙이 `docs/game-support-type-policy.md` 에 있고, 배지·상세
 * 화면이 이미 그걸로 그린다. 여기서 다시 유도하면 같은 게임이 화면마다 다른 방식으로
 * 분류된다(실제로 `official` 불리언으로 직접 세어 봤더니 34건이 1건으로 어긋났다).
 */
const SUPPORT_TYPE_FACET: Record<SupportType, string> = {
  [SupportType.Native]: "native",
  [SupportType.Mod]: "mod",
  [SupportType.OfficialMod]: "official-mod",
  [SupportType.AiPowered]: "ai-powered",
};

const supportTypeOf = (game: BhapticsContent): string | undefined =>
  SUPPORT_TYPE_FACET[
    SupportTypeUtil.getSupportType(game.supportedPlatforms ?? [])
  ];

const devicesOf = (game: BhapticsContent): string[] =>
  game.compatibleDevices ?? [];

/** SDK Genre facet 은 소문자(`shooting`), 게임 데이터는 표시형(`Shooting`)이다. */
const genresOf = (game: BhapticsContent): string[] =>
  (game.categories ?? []).map((c) => c.toLowerCase());

const VALUE_OF: Record<VrFilterGroupKey, (game: BhapticsContent) => string[]> =
  {
    platform: platformsOf,
    supportType: (g) => {
      const t = supportTypeOf(g);
      return t ? [t] : [];
    },
    device: devicesOf,
    genre: genresOf,
  };

/** 한 그룹의 선택이 게임을 통과시키는가. 빈 선택 = 그 그룹은 안 거른다(OR 그룹의 항등원). */
export const passesGroup = (
  game: BhapticsContent,
  key: VrFilterGroupKey,
  selected: string[],
) => {
  if (selected.length === 0) return true;
  const owned = VALUE_OF[key](game);
  // 같은 그룹은 **OR** — SDK 와 같은 의미다(실측: `quest,steam` = 318 = 합집합).
  return selected.some((v) => owned.includes(v));
};

export type VrGamesFilterState = {
  groups: VrFilterGroup[];
  games: BhapticsContent[];
  isLoading: boolean;
  /** 전체(필터 전) VR 게임 수. 「N개의 게임」은 `games.length` 를 쓴다. */
  totalCount: number;
  /** 확정된 검색어(`?q=`). 공유 링크·뒤로가기가 이걸 쓴다. */
  search: string;
  /**
   * ★**입력 중인 값 — 목록이 타이핑마다 이걸로 좁힌다** (#5593 · 2026-09-07).
   *
   * `search`(URL)와 **일부러 분리**한다. 종전엔 입력값이 URL 에서 왔고, 그래서 입력이
   * 버려졌다(아래 훅 주석의 실측). PC 판(`use-pc-games-filters.ts`)과 같은 구조다.
   */
  draft: string;
  /** 확정 상태 키 — 타이핑(`draft`)은 안 들어간다 (#5672). 정의 위 주석 참조. */
  committedKey: string;
  setDraft: (v: string) => void;
  gameKeyOnly: boolean;
  gameKeyCount: number;
  hasAnyFilter: boolean;
  toggle: (key: VrFilterGroupKey, value: string) => void;
  setSearch: (v: string) => void;
  toggleGameKey: () => void;
  reset: () => void;
  sortBy: string;
  setSortBy: (v: string) => void;
};

const QUERY_KEYS: Record<VrFilterGroupKey, string> = {
  platform: "platform",
  supportType: "support",
  device: "device",
  genre: "genre",
};

const readList = (v: string | string[] | undefined): string[] =>
  (Array.isArray(v) ? v[0] : v)?.split(",").filter(Boolean) ?? [];

/** 그룹별 허용 값 — `GROUP_DEFS` 에서 파생한다(손으로 적지 않는다). */
const ALLOWED_VALUES: Record<
  VrFilterGroupKey,
  Set<string>
> = Object.fromEntries(
  GROUP_DEFS.map((d) => [d.key, new Set(d.options.map((o) => o.value))]),
) as Record<VrFilterGroupKey, Set<string>>;

/**
 * URL 이 준 값 중 **화면에 칩이 있는 것만** 남긴다 (#5539 R1 m1).
 *
 * 훅 밖으로 뺀 이유는 **가드가 직접 잡을 수 있게** 하기 위해서다 —
 * `passesGroup` 과 같은 표기다. 라우터를 모킹하지 않고 이 불변식만 본다.
 */
export const sanitizeSelection = (
  key: VrFilterGroupKey,
  raw: string[],
): string[] => raw.filter((v) => ALLOWED_VALUES[key].has(v));

export function useVrGamesFilters(): VrGamesFilterState {
  const router = useRouter();
  const query = router.query;

  /**
   * **확정 상태 키** — 타이핑(`draft`)은 들어가지 않는다 (#5672).
   *
   * ## 왜 필요한가
   *
   * `useKeepResultsInView` 는 종전 `games.length` 로 키를 잡았는데, 이 화면의 그리드는
   * **`draft` 로 라이브 필터**하므로(아래 `byText`) 그 수가 **글자마다** 바뀐다. 그래서
   * 「결과가 화면 밖에 남으면 되돌린다」가 타이핑 한 글자마다 발화했다.
   *
   * 실측(2026-09-10 · 1440×900 · `/games/pc/games/` · 리스트 중간 `y=1500`):
   *
   * ```
   *   입력 전   결과바 vpBottom = -190  ≤ 가림선 137   → 발화조건 **이미 참**
   *             그리드 vpTop -176 · vpBottom 2165      → 결과는 화면을 꽉 채운다
   * ```
   *
   * 결과 바(개수·정렬 줄)는 카드 **위**에 있으니 목록 안으로 스크롤하면 이미 화면 밖이다.
   * 즉 평범하게 훑던 상태에서 조건이 참이고, 글자를 치면 리스트 시작으로 되돌아갔다 —
   * 그 훅의 docblock 이 *"여기서 스크롤을 건드리면 글자마다 화면이 튀는 그 증상을
   * 되살린다"* 고 스스로 경고한 자리다.
   *
   * ## 요구사항은 「확정」에만 있다
   *
   * ```
   *   #5609  사용자 지정  "검색 **버튼 눌렀을때** 딱 결과 위치부터 보이면"   → 확정
   *   #5676  작성자 제보  "검색어 넣으니 스티키가 이동한다"                  → 튐 처방
   * ```
   *
   * 타이핑에 스크롤하라는 요구는 없다(사용자 확인 2026-09-10: *"리스트 중간에서 검색
   * 「ㄴ」 쓰면 위로 스크롤돼서 리스트 시작부터 보이게 하는 게 이상하다"*).
   *
   * ## URL 이 곧 확정 상태다
   *
   * 이 훅의 확정 값은 전부 `push({ ... })` 로 URL 에 들어간다(`q`·`support`·`genre`·
   * `sort` …). 그래서 **쿼리를 정렬해 직렬화**한 것이 「확정 상태」의 정확한 키다 —
   * 값을 하나씩 열거하면 축이 늘 때(장르 · 정렬 · 판매 여부) 조용히 빠진다.
   *
   * ⚠️ `draft` 는 URL 에 **일부러 안 넣는다**(위 `draft` 주석 — 넣으면 글자가 유실된다).
   * 그래서 이 키에 타이핑이 섞일 수 없다.
   */
  const committedKey = Object.entries(query)
    .map(([k, v]) => `${k}=${String(v)}`)
    .sort()
    .join("&");

  /**
   * ★**기본값이 정렬 목록에 없어서 라벨이 빈 채로 나갔다.**
   *
   * 종전 기본값은 `weeklyPlayCount` 인데 사용자에게 보이는 목록
   * (`GAME_SORT_BY_FALLBACK.options`)에는 그 값이 **없다.** 결과가 둘이었다:
   *
   * 1. `options.find(...)?.description ?? ""` 가 빈 문자열이라 정렬 컨트롤이
   *    **아이콘만** 보였다 — 시안(`29889:101026`·`29942:17612`)은 `Most Played` 다
   * 2. 한 번 다른 정렬로 바꾸면 **되돌아올 방법이 없다**(목록에 없으니 못 고른다)
   *
   * `monthlyPlayCount` 가 그 목록에서 `Most Played` 이고, 형제 소비처 두 곳
   * (`useBhapticsContentsWebView` · `game-api`)도 이미 이 값을 기본으로 쓴다.
   * 즉 이 화면만 예외였다.
   *
   * ⚠️ 이 결함의 클래스는 형제 파일에 **이미 주석으로 적혀 있었다**
   * (`useBhapticsContentsWebView.ts` — *"기본 정렬이 이 목록에 없으면 선택 라벨이
   * 잘못 표시된다"*). 주석은 재발을 막지 못했으므로
   * `__tests__/constants/game-sort-default.test.ts` 가 **소스 전수로** 잠근다.
   */
  const sortBy = (query.sort as string) || "monthlyPlayCount";
  const search = ((query.q as string) || "").trim();

  /**
   * ★**입력값은 로컬 상태다 — URL 을 왕복하지 않는다** (#5593 · 2026-09-07).
   *
   * 종전엔 `vr-games-search.tsx` 가 `value={state.search}` + `onChange={setSearch}` 로
   * **키 입력마다 라우터를 왕복**했다. `value` 의 출처가 URL 이라 다음 키가 들어올 때
   * 아직 옛 값이고, 그 사이 입력이 버려진다. 실측(2026-09-07 · dev · 1440):
   *
   * ```
   *   빠른 타이핑    "half" 입력 → 입력값 **"f"**        3글자 유실
   *   느린 타이핑    h → ha → hal → half                 URL 이 따라온다
   *   'half life'    ' ' 에서 value 가 "half" 로 되돌아가고
   *                  다음 글자가 바로 붙어 결과 **"halflife"**
   * ```
   *
   * 두 번째가 더 나쁘다 — 위 `search` 의 `.trim()` 이 뒤 공백을 **즉시** 자르므로
   * 공백이 `value` 에 살아남지 못한다. 즉 **두 단어 게임 이름을 검색할 방법이 없었다.**
   *
   * 그래서 PC 판과 같은 구조로 바꿨다: 입력값은 로컬, 목록은 이 값으로 좁히고, URL 은
   * 확정(`setSearch`) 시에만 쓴다. 공유 링크(`?q=`)와 뒤로가기는 그대로 동작한다.
   */
  const [draft, setDraft] = useState(search);

  /**
   * 뒤로가기·리셋 등으로 **URL 이 바뀌면** 입력도 따라간다.
   *
   * ⚠️ `useEffect` 로 하지 않는다 — `react-hooks/set-state-in-effect` 가 막고, 입력이
   * 옛 값으로 한 프레임 보였다가 바뀐다. 렌더 중에 직전 값과 비교해 맞춘다(React 문서화
   * 패턴 · PC 판과 같다).
   */
  const [syncedSearch, setSyncedSearch] = useState(search);
  if (search !== syncedSearch) {
    setSyncedSearch(search);
    setDraft(search);
  }
  const gameKeyOnly = query["only-sellable"] === "true";

  /**
   * ★**URL 값은 `GROUP_DEFS` 에 있는 것만 받는다** (#5539 R1 m1).
   *
   * ## 정제하지 않으면 「보이지 않는 필터」가 된다
   *
   * 종전엔 쿼리를 그대로 `selected` 로 썼다. 그러면 화면에서 걷어낸 값이 URL 에
   * 남아 있을 때 **목록은 좁혀지는데 그 이유가 화면에 없다** — 행이 `def.options`
   * 파생이라 체크할 칩 자체가 없기 때문이다. 실측(2026-09-04 dev):
   *
   * ```
   *   ?device=Hand           목록 8건으로 좁아짐 · 체크된 칩 0 · Reset 은 활성
   *   ?support=ai-powered    ★목록 0건(빈 화면) · 체크된 칩 0
   * ```
   *
   * `Reset` 으로 풀리긴 하지만, 사용자는 **왜 좁아졌는지 알 방법이 없다.**
   * 두 번째가 더 세다 — 스테일 링크 하나로 빈 목록이 된다.
   *
   * ## 그래서 읽을 때 교집합을 취한다
   *
   * 모르는 값은 **무해한 no-op** 이 되고, 화면(칩)과 상태(목록)가 항상 일치한다.
   * 잃는 것은 없다 — 칩이 없는 값은 어차피 사용자가 켜지도 끄지도 못한다.
   *
   * ⚠️ 이건 **한 훅이 네 그룹을 다 다루므로** `device`·`support` 양쪽에 함께 걸린다.
   * #5537(`ai-powered` 제거)과 #5539(`Tactosy for Hands` 제거)가 만든 같은 기전을
   * 한 자리에서 닫는다.
   */
  const selected = useMemo(() => {
    const pick = (key: VrFilterGroupKey) =>
      sanitizeSelection(key, readList(query[QUERY_KEYS[key]]));

    return {
      platform: pick("platform"),
      supportType: pick("supportType"),
      device: pick("device"),
      genre: pick("genre"),
    } as Record<VrFilterGroupKey, string[]>;
  }, [query]);

  // 서버에는 **기본 집합만** 묻는다. 좁히기는 아래에서 클라가 한다.
  const { data, isLoading } = useGames({
    filters: [...VR_PLATFORM_VALUES],
    sortBy,
  });
  const all = useMemo(() => data ?? [], [data]);

  const byText = useMemo(() => {
    // ★ `search`(확정)가 아니라 `draft`(입력 중)다 — 위 주석.
    const needle = draft.trim().toLowerCase();
    if (!needle) return all;
    return all.filter((g) => g.name?.toLowerCase().includes(needle));
  }, [all, draft]);

  /**
   * 「판매 가능만」 = 게임 키가 있고 **이 국가에서 막히지 않았고** **이 통화의 가격이 있는** 게임.
   * ★카드 가격 배지(`GameCard` `GameCardResellBanner`)와 **같은 세 조건·같은 함수**다 — 목록에 뜬
   * 카드는 전부 배지를 단다(#5866 R1). 하나라도 갈리면 「목록엔 있는데 배지가 없는 카드」가 다시 생긴다.
   *
   * 종전엔 `!!g.gameResell`(키 존재)만 봤다 → `disableCountryList` 에 KR 이 든 게임이 한국의
   * 「판매 가능만」 목록에 그대로 떴다(카드는 가격 배지 없이). prod 실측(2026-09-30): 판매 게임 6개가
   * **전부** KR 차단이라 한국에선 빈 목록이어야 했다. 국가 판정은 `useSellableGames` 와 같은
   * `filterSellableGames` 하나다 — 카드 배지(`GameCard`)도 같은 목록으로 숨긴다.
   *
   * 통화 조건은 지금(2026-09-30 prod, GE flag OFF) 걸리는 게임이 0이다 — KRW 가 없는 게임은 전부
   * KR 차단이다. 걸리는 건 GE ON 후 EU·GB 다: 게임 카탈로그엔 EUR/GBP 가격이 없고, GE 체크아웃도
   * 통화 불일치 라인을 드롭한다(`models/GameResell.ts`) — 그 나라에선 실제로 살 수 없다.
   *
   * `countryCode` 는 SSR·첫 렌더에서 `""`(마운트 후 쿠키로 채운다, `locale-store.ts`)라
   * 그동안은 국가로 거르지 않는다 — hydration 불일치를 만들지 않으려는 스토어 설계를 따른다.
   * 통화도 flag 로딩 창에서 USD 였다가 바뀐다(`useCurrency`) — 같은 부류의 「잠깐 보였다 사라짐」이다.
   */
  const { countryCode } = useLocale();
  const currency = useCurrency();
  const sellable = useMemo(
    () =>
      filterSellableGames(
        byText.filter(
          (g) =>
            !!g.gameResell &&
            GameUtil.hasGameResellPrice(g.gameResell, currency),
        ),
        countryCode,
      ),
    [byText, countryCode, currency],
  );

  const byGameKey = gameKeyOnly ? sellable : byText;

  /**
   * 한 그룹을 **제외한** 나머지 조건을 적용한 집합. 그 그룹의 개수는 여기서 센다 —
   * 표준 facet 카운트 의미이고, 이래야 *"Quest 를 켜면 몇 개가 남나"* 를 답한다.
   * (같은 그룹의 다른 선택으로 자기 개수가 0 이 되어 더 못 고르는 상태를 막는다.)
   */
  const setExcluding = useMemo(() => {
    const keys = GROUP_DEFS.map((g) => g.key);
    return Object.fromEntries(
      keys.map((k) => [
        k,
        byGameKey.filter((g) =>
          keys.every((other) =>
            other === k ? true : passesGroup(g, other, selected[other]),
          ),
        ),
      ]),
    ) as Record<VrFilterGroupKey, BhapticsContent[]>;
  }, [byGameKey, selected]);

  const games = useMemo(
    () =>
      byGameKey.filter((g) =>
        GROUP_DEFS.every((def) => passesGroup(g, def.key, selected[def.key])),
      ),
    [byGameKey, selected],
  );

  const groups = useMemo<VrFilterGroup[]>(
    () =>
      GROUP_DEFS.map((def) => ({
        key: def.key,
        label: def.label,
        defaultOpen: def.defaultOpen,
        rows: def.options.map((o) => ({
          value: o.value,
          label: o.description,
          checked: selected[def.key].includes(o.value),
          count: setExcluding[def.key].filter((g) =>
            VALUE_OF[def.key](g).includes(o.value),
          ).length,
        })),
      })),
    [selected, setExcluding],
  );

  const push = (next: Record<string, string | undefined>) => {
    const merged: Record<string, string> = {};
    for (const [k, v] of Object.entries({ ...query, ...next })) {
      // 경로 파라미터는 쿼리로 되돌리지 않는다(이 라우트엔 없지만 방어).
      if (typeof v === "string" && v) merged[k] = v;
    }
    // ★ 목적지를 하드코딩하지 않는다 — 현재 경로에 머문다(위 주석 §1).
    router.push({ pathname: router.pathname, query: merged }, undefined, {
      shallow: true,
      scroll: false,
    });
  };

  const toggle = (key: VrFilterGroupKey, value: string) => {
    const cur = selected[key];
    const next = cur.includes(value)
      ? cur.filter((v) => v !== value)
      : [...cur, value];
    push({ [QUERY_KEYS[key]]: next.join(",") || undefined });
  };

  const hasAnyFilter =
    gameKeyOnly ||
    // ★확정이 아니라 **입력**이 기준이다 — 목록이 입력으로 좁혀지므로, 리셋 노출도
    //   같은 값을 따라야 「좁혀졌는데 리셋이 안 뜨는」 상태가 안 생긴다.
    !!draft.trim() ||
    GROUP_DEFS.some((d) => selected[d.key].length > 0);

  return {
    committedKey,
    groups,
    games,
    isLoading,
    totalCount: all.length,
    search,
    draft,
    setDraft,
    gameKeyOnly,
    gameKeyCount: sellable.length,
    hasAnyFilter,
    toggle,
    setSearch: (v: string) => push({ q: v || undefined }),
    toggleGameKey: () =>
      push({ "only-sellable": gameKeyOnly ? undefined : "true" }),
    reset: () =>
      push({
        platform: undefined,
        support: undefined,
        device: undefined,
        genre: undefined,
        q: undefined,
        "only-sellable": undefined,
      }),
    sortBy,
    setSortBy: (v: string) => push({ sort: v }),
  };
}
